import { Progress, usePrevious } from "@chakra-ui/react";
import { clamp } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigation } from "react-router-dom";
import zIndices from "theme/zIndices";
import useDelayedState from "use-delayed-state";
import { useInterval } from "usehooks-ts";

const INCREMENT_INTERVAL_MS = 300;
const INDICATOR_SHOW_DELAY_MS = 100;
const INDICATOR_HIDE_DELAY_MS = 200;

export const AppLoadingIndicator: React.FC = () => {
  const { state } = useNavigation();
  const isLoading = state === "loading";

  const { isIndicatorVisible, indicatorProgress } =
    useLoadingIndicator(isLoading);
  const prevIndicatorProgress = usePrevious(indicatorProgress);

  return (
    <Progress
      size="xs"
      position="fixed"
      top="0"
      left="0"
      right="0"
      height="2px"
      bg="none"
      zIndex={zIndices.appLoadingIndicator}
      value={indicatorProgress}
      opacity={isIndicatorVisible ? 1 : 0}
      sx={{
        "& > div[role=progressbar]": {
          // Animate when progress (width) changes
          transitionProperty:
            indicatorProgress > prevIndicatorProgress ? "width" : "none",
          transitionTimingFunction: "ease",
          transitionDuration: "200ms",
        },
      }}
    />
  );
};

const useLoadingIndicator = (isLoading: boolean) => {
  const [count, setCount] = useState(0);
  const [
    isIndicatorVisible,
    setIndicatorVisible,
    cancelIndicatorDelayedVisible,
  ] = useDelayedState(false);

  const prevIsLoading = usePrevious(isLoading);

  // <Progress /> is 0-100, count is 0-1
  const indicatorProgress = Math.floor((isLoading ? count : 1) * 100);

  // console.log(count, indicatorProgress);

  useEffect(() => {
    if (isLoading) {
      // Do not show the indicator unless the page is still loading after a short delay
      setIndicatorVisible(true, INDICATOR_SHOW_DELAY_MS);
    } else {
      cancelIndicatorDelayedVisible();
      setCount(1);
      // Keep the indicator visible for a short delay after loading is complete
      if (isIndicatorVisible) {
        setIndicatorVisible(false, INDICATOR_HIDE_DELAY_MS);
      }
    }
  }, [
    cancelIndicatorDelayedVisible,
    isIndicatorVisible,
    isLoading,
    setCount,
    setIndicatorVisible,
  ]);

  useEffect(() => {
    if (isIndicatorVisible && count === 0) {
      // Don't wait for interval for first increment
      setCount(0.1);
    } else if (!isIndicatorVisible || (prevIsLoading && !isLoading)) {
      setCount(0);
    }
  }, [count, isIndicatorVisible, isLoading, prevIsLoading]);

  useInterval(
    () => {
      // based on https://github.com/rstacruz/nprogress/blob/master/nprogress.js
      setCount((n) => {
        let amount = 0;
        if (n >= 0 && n < 0.2) {
          amount = 0.1;
        } else if (n >= 0.2 && n < 0.5) {
          amount = 0.04;
        } else if (n >= 0.5 && n < 0.8) {
          amount = 0.02;
        } else if (n >= 0.8 && n < 0.99) {
          amount = 0.005;
        } else {
          amount = 0;
        }
        return clamp(n + amount, 0, 0.994);
      });
    },
    isIndicatorVisible && count > 0 && count < 1 ? INCREMENT_INTERVAL_MS : null,
  );

  return { isIndicatorVisible, indicatorProgress } as const;
};
